import React, {
	createContext,
	Dispatch,
	SetStateAction,
	useContext,
	useEffect,
	useState,
} from 'react'
import { ChangeOrderFilters, ChangeOrder } from '../../types/Work/ChangeOrder'
import { getSurveyAssignees } from '../../services/quantitySurveyService'
import { RequestWorker } from '../../types/Work/Request'
import { getChangeOrders } from '../../services/changeOrderService'
import { ManualChangeOrderInfo } from '../../types/Work/ChangeOrder'

type ChangeOrderContextType = {
	filters: ChangeOrderFilters
	setFilters: Dispatch<SetStateAction<ChangeOrderFilters>>
	assignees: RequestWorker[]
	changeOrders: ChangeOrder[]
	totalCount: number
	isLoading: boolean
	selectedChangeOrder: number | null
	setSelectedChangeOrder: Dispatch<SetStateAction<number | null>>
	detailsToRevise: ManualChangeOrderInfo | undefined
	setDetailsToRevise: Dispatch<SetStateAction<ManualChangeOrderInfo | undefined>>
	loadChangeOrders: () => Promise<void>
	revisions: Map<number, ChangeOrder[]>
	setRevisions: Dispatch<SetStateAction<Map<number, ChangeOrder[]>>>
}

export const ChangeOrderContext = createContext<ChangeOrderContextType | undefined>(undefined)

export const ChangeOrderProvider = ({
	children,
	initialSurveyId,
	key,
}: {
	children: React.ReactNode
	initialSurveyId: number | null
	key: number
}) => {
	const [filters, setFilters] = useState<ChangeOrderFilters>({
		status: null,
		reportedById: null,
		reportedOn: null,
		searchWorkItem: null,
		page: 0,
		surveyId: initialSurveyId,
	})
	const [assignees, setAssignees] = useState<RequestWorker[]>([])
	const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([])
	const [totalCount, setTotalCount] = useState<number>(0)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [selectedChangeOrder, setSelectedChangeOrder] = useState<number | null>(null)
	const [detailsToRevise, setDetailsToRevise] = useState<ManualChangeOrderInfo>()
	const [revisions, setRevisions] = useState<Map<number, ChangeOrder[]>>(new Map())

	useEffect(() => {
		const fetchAssignees = async () => {
			const assigneesResponse = await getSurveyAssignees()
			setAssignees(assigneesResponse)
		}
		fetchAssignees()
	}, [])

	

	const loadChangeOrders = async () => {
		const changeOrdersResponse = await getChangeOrders(filters)
		setChangeOrders(changeOrdersResponse.changeOrders)
		setTotalCount(changeOrdersResponse.allCount)
	}

	useEffect(() => {
		const timeoutId = setTimeout(async () => {
			setIsLoading(true)
			try {
				await loadChangeOrders()
			} catch (error) {
				console.error('Error fetching change orders:', error)
			} finally {
				setIsLoading(false)
			}
		}, 500)

		return () => clearTimeout(timeoutId)
	}, [filters, key])

	return (
		<ChangeOrderContext.Provider
			value={{
				filters,
				setFilters,
				assignees,
				changeOrders,
				totalCount,
				isLoading,
				selectedChangeOrder,
				setSelectedChangeOrder,
				loadChangeOrders,
				detailsToRevise,
				setDetailsToRevise,
				revisions,
				setRevisions,
			}}
		>
			{children}
		</ChangeOrderContext.Provider>
	)
}

export const useChangeOrderContext = () => {
	const context = useContext(ChangeOrderContext)
	if (!context) {
		throw new Error('useChangeOrderContext must be used within a ChangeOrderProvider')
	}
	return context
}
